/* global axios */
import ApiClient from './ApiClient';

class TicketsAPI extends ApiClient {
  constructor() {
    super('tickets', { accountScoped: true });
  }

  getTicket(id) {
    return axios.get(`${this.url}/${id}`);
  }

  // Get all tickets
  getAllTickets({
    page,
    status_id,
    priority_id,
    assignee_id,
    team_id,
    contact_id,
    business_account_id,
    board_id,
    search,
    per_page,
    status,
    priority,
    paginate,
    custom_attributes,
  } = {}) {
    const params = {
      page,
      search,
      per_page,
    };

    if (board_id != null && board_id !== '') {
      params.board_id = board_id;
    }
    
    if (paginate !== undefined) {
      params.paginate = paginate;
    }

    // Add array filters if they exist and have values (IDs-based)
    if (status_id && Array.isArray(status_id) && status_id.length > 0) {
      params.status_id = status_id;
    }
    if (priority_id && Array.isArray(priority_id) && priority_id.length > 0) {
      params.priority_id = priority_id;
    }
    if (assignee_id && Array.isArray(assignee_id) && assignee_id.length > 0) {
      params.assignee_id = assignee_id;
    }
    if (team_id && Array.isArray(team_id) && team_id.length > 0) {
      params.team_id = team_id;
    }
    if (contact_id && Array.isArray(contact_id) && contact_id.length > 0) {
      params.contact_id = contact_id;
    }
    if (
      business_account_id &&
      Array.isArray(business_account_id) &&
      business_account_id.length > 0
    ) {
      params.business_account_id = business_account_id;
    }

    // Also support name-based filters used by the UI/store
    if (status && Array.isArray(status) && status.length > 0) {
      params.status = status;
    }
    if (priority && Array.isArray(priority) && priority.length > 0) {
      params.priority = priority;
    }

    // Add custom_attributes filter if it exists and has values
    // Send as nested object - Rails will parse it as ActionController::Parameters
    if (
      custom_attributes &&
      typeof custom_attributes === 'object' &&
      Object.keys(custom_attributes).length > 0
    ) {
      params.custom_attributes = custom_attributes;
    }

    return axios.get(this.url, { params });
  }

  get(params) {
    return this.getAllTickets(params);
  }

  create(ticketData) {
    return axios.post(this.url, ticketData);
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}`, data);
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  changeStatus(id, newStatus) {
    return axios.patch(`${this.url}/${id}/change_status`, {
      new_status: newStatus,
    });
  }

  changePriority(id, newPriority) {
    return axios.patch(`${this.url}/${id}/change_priority`, {
      new_priority: newPriority,
    });
  }

  addComment(id, content, attachments = []) {
    const formData = new FormData();
    formData.append('content', content || '');

    attachments.forEach((attachment, index) => {
      if (attachment.blob_signed_id) {
        formData.append(
          `attachments[${index}][blob_signed_id]`,
          attachment.blob_signed_id
        );
      }
      if (attachment.filename) {
        formData.append(`attachments[${index}][filename]`, attachment.filename);
      }
      if (attachment.content_type) {
        formData.append(
          `attachments[${index}][content_type]`,
          attachment.content_type
        );
      }
    });

    return axios.post(`${this.url}/${id}/add_comment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  getStatuses() {
    return axios.get(`${this.url}/statuses`);
  }

  getPriorities() {
    return axios.get(`${this.url}/priorities`);
  }

  getTeams() {
    return axios.get(`${this.url}/teams`);
  }

  getUsers() {
    return axios.get(`${this.url}/users`);
  }

  getContacts() {
    return axios.get(`${this.url}/contacts`);
  }

  // Get business accounts
  getBusinessAccounts() {
    return axios.get(`${this.url}/business_accounts`);
  }

  // Get SLA policies
  getSlaPolicies() {
    return axios.get(`${this.url}/sla_policies`);
  }

  // Change ticket SLA policy
  changeSlaPolicy(id, newSlaPolicy) {
    return axios.patch(`${this.url}/${id}/change_sla_policy`, {
      new_sla_policy: newSlaPolicy,
    });
  }

  // Remove ticket SLA policy
  removeSlaPolicy(id) {
    return axios.delete(`${this.url}/${id}/remove_sla_policy`);
  }

  // Get SLA monitoring data
  getSlaMonitoringData(params = {}) {
    return axios.get(`${this.url}/sla_monitoring_data`, { params });
  }

  getTicketHistory(id) {
    return axios.get(`${this.url}/${id}/history`);
  }

  change_assignee(id, newAssignee) {
    return axios.patch(`${this.url}/${id}/change_assignee`, {
      new_assignee: newAssignee,
    });
  }

  changeTeam(id, newTeam) {
    return axios.patch(`${this.url}/${id}/change_team`, {
      new_team: newTeam,
    });
  }

  changeContact(id, newContact) {
    return axios.patch(`${this.url}/${id}/change_contact`, {
      new_contact: newContact,
    });
  }

  changeBoard(id, boardId, boardColumnId) {
    return axios.patch(`${this.url}/${id}/change_board`, {
      board_id: boardId,
      board_column_id: boardColumnId,
    });
  }

  removeAssignee(id) {
    return axios.delete(`${this.url}/${id}/remove_assignee`);
  }

  removeTeam(id) {
    return axios.delete(`${this.url}/${id}/remove_team`);
  }

  removeContact(id) {
    return axios.delete(`${this.url}/${id}/remove_contact`);
  }

  changeBusinessAccount(id, newBusinessAccount) {
    return axios.patch(`${this.url}/${id}/change_business_account`, {
      new_business_account: newBusinessAccount,
    });
  }

  removeBusinessAccount(id) {
    return axios.delete(`${this.url}/${id}/remove_business_account`);
  }

  updateCustomAttributes(id, customAttributes) {
    return axios.patch(`${this.url}/${id}/update_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  deleteCustomAttribute(id, attributeKey) {
    return axios.delete(`${this.url}/${id}/custom_attributes/${attributeKey}`);
  }

  changeTitle(id, newTitle) {
    return axios.patch(`${this.url}/${id}/change_title`, {
      new_title: newTitle,
    });
  }

  changeDescription(id, newDescription) {
    return axios.patch(`${this.url}/${id}/change_description`, {
      new_description: newDescription,
    });
  }

  changeService(id, serviceId, subServiceId) {
    return axios.patch(`${this.url}/${id}/change_service`, {
      service_id: serviceId,
      sub_service_id: subServiceId,
    });
  }
}

export default new TicketsAPI();
